import { BrowserRouter, RouterProvider } from 'react-router';
import { ErrorBoundary } from './components/ErrorBoundary';

/**
 * Client views render outside the firm RouterProvider, yet the grid table and
 * the CEL onboarding page call react-router hooks. Without a Router those hooks
 * throw, and without a boundary the whole app went to a blank page. This gives
 * client-side trees both.
 */
function ClientShell({ name, children }: { name: string; children: ReactNode }) {
  return (
    <ErrorBoundary name={name}>
      <BrowserRouter>{children}</BrowserRouter>
    </ErrorBoundary>
  );
}
import { createAppRouter } from './routes';
import { useState, useEffect, type ReactNode } from 'react';
import { supabase } from './utils/supabase/client';
import { projectId } from './utils/supabase/info';
import { getAuthHeaders, storeSessionToken } from './utils/apiHeaders';
import { destroySession, clearSessionFromLocalStorage, getSessionFromLocalStorage } from './utils/sessionManager';
import { getTenantFromHostname, getTenantCompanyId, isAdminDomain, isSystemAdminDomain } from './utils/tenantDetection';
import { setAuthState } from './utils/authState';
import { unsavedChangesManager } from './utils/unsavedChanges';
import { getCrossTabSync } from './utils/crossTabSync';
import { getPersistedCompany, persistActiveCompany, clearPersistedCompany } from './components/CompanyPicker';
import { 
  hasRole, 
  hasAnyRole, 
  isFirmUser, 
  isClientUser, 
  isSystemAdmin, 
  isBillingAdminOnly,
  getActiveViewRole,
  isActiveViewSystemAdmin,
  isActiveViewFirmUser,
  isActiveViewBillingAdminOnly,
} from './utils/roleUtils';

// Import components
import { Bootstrap } from './components/Bootstrap';
import { SignupPage } from './components/SignupPage';
import { Login } from './components/Login';
import { OnboardingWizard } from './components/OnboardingWizard';
import { SessionExpirationModal } from './components/SessionExpirationModal';
import { SystemAdminRouter } from './components/SystemAdminRouter';
import { LegacyAdminRedirect } from './components/LegacyAdminRedirect';
import { ConsultantDashboard } from './components/ConsultantDashboard';
import { ClientDashboardNew } from './components/ClientDashboardNew';
import { ClientDetailView } from './components/ClientDetailView';
import { UserDebugInfo } from './components/UserDebugInfo';
import { BillingAdminDashboard } from './components/BillingAdminDashboard';
import { BillingAdminView } from './components/BillingAdminView';
import { FirmContextGate } from './components/FirmContextGate';
import { TopNavigation } from './components/TopNavigation';
import { OAuthQuickTest } from './components/OAuthQuickTest';
import { UserRecovery } from './components/UserRecovery';
import { PasswordReset } from './components/PasswordReset';
import { ClientSOWViewer } from './components/ClientSOWViewer';
import { QuickSubdomainSetup } from './components/QuickSubdomainSetup';
import { NotificationPreferencesPage } from './components/NotificationPreferencesPage';
import { TestingDashboard } from './components/TestingDashboard';
import { InternalTestHarness } from './components/InternalTestHarness';
import { ControlGridPage } from './components/ControlGridPage';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { AIDataUse } from './components/AIDataUse';
import { TermsOfService } from './components/TermsOfService';
import { MarketingHomepage } from './components/marketing/MarketingHomepage';
import { BookDemo } from './components/marketing/BookDemo';
import { QuestionBankPage } from './pages/QuestionBankPage';
import { QueryProvider } from './providers/QueryProvider';
import { CELOnboardingPage } from './components/CELOnboardingPage';
import { Button } from './components/ui/button';
import { Shield } from 'lucide-react';
import { useImpersonation } from './components/ImpersonationContext';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { ImpersonationProvider } from './components/ImpersonationContext';

// Note: Helper functions moved to /utils/roleUtils.ts for consistency

// -----------------------------------------------------------------------
// CENTRALIZED VIEW RENDERER
// Determines which UI experience to show based on activeRole.
// isSystemAdmin() is used for AUTHORIZATION (can this user access this page?).
// getActiveViewRole() is used for VIEW SELECTION (which UI do they see?).
// -----------------------------------------------------------------------
function renderViewForActiveRole(
  user: any,
  accessToken: string,
  company: any,
  handleLogout: () => void,
) {
  const activeRole = getActiveViewRole(user);

  console.log('[ROLE_VIEW] ===== View Selection =====');
  console.log('[ROLE_VIEW] baseRole:', user?.baseRole || 'none');
  console.log('[ROLE_VIEW] isAdmin:', user?.isAdmin);
  console.log('[ROLE_VIEW] activeRole:', activeRole);
  console.log('[ROLE_VIEW] user.role:', user?.role);

  // 1) activeRole is systemAdmin → System Admin experience
  if (activeRole === 'systemAdmin' || activeRole === 'system_admin') {
    console.log('[ROLE_VIEW] selected router/page: SystemAdminRouter');
    return (
      <SystemAdminRouter
        accessToken={accessToken}
        currentUser={user}
        onLogout={handleLogout}
      />
    );
  }

  // 2) activeRole is billingAdmin only → Billing experience
  if (activeRole === 'billingAdmin') {
    console.log('[ROLE_VIEW] selected router/page: BillingAdminView');
    return (
      <BillingAdminView
        accessToken={accessToken}
        currentUser={user}
        onLogout={handleLogout}
      />
    );
  }

  // 3) activeRole is a firm role → Consultant/Firm experience via React Router
  if (isActiveViewFirmUser(user)) {
    console.log('[ROLE_VIEW] selected router/page: ConsultantDashboard (via React Router)');
    const path = window.location.pathname;
    const isViewAs = !!(user?.isAdmin && user?.activeRole !== 'systemAdmin');
    console.log('[ROLE_SWITCH_FLOW] firmId present:', !!(user?.firmId || user?.consultingFirmId));
    console.log('[ROLE_SWITCH_FLOW] subdomain present:', !!user?.subdomain);
    console.log('[ROLE_SWITCH_FLOW] current path for RouterProvider:', path);
    console.log('[CF_ADMIN_CONTEXT] AppRouter firmId:', user?.firmId || user?.consultingFirmId || 'MISSING');
    if (isViewAs) {
      console.log('[VIEW_AS_CONTEXT] system admin in view-as mode, activeRole:', user?.activeRole, ', viewAsMode:', user?.viewAsMode, ', firmId:', user?.firmId || 'NONE');
    }

    // Special route for Question Bank
    if (path === '/question-bank' || path.startsWith('/question-bank')) {
      return (
        <FirmContextGate currentUser={user} accessToken={accessToken}>
          <QueryProvider>
            <QuestionBankPage firmId={user.firmId || user.consultingFirmId} />
          </QueryProvider>
        </FirmContextGate>
      );
    }

    const router = createAppRouter({
      accessToken,
      currentUser: user,
      onLogout: handleLogout,
    });
    return (
      <FirmContextGate
        currentUser={user}
        accessToken={accessToken}
        onFirmResolved={(firmId, firmName) => {
          // Update user state in-memory so children get the context immediately
          console.log('[CF_ADMIN_CONTEXT] FirmContextGate resolved, updating user state:', { firmId, firmName });
        }}
      >
        <RouterProvider router={router} />
      </FirmContextGate>
    );
  }

  // 4) System-admin client view-as — explicit branch.
  //    A system admin who used the role switcher to preview a client-side
  //    role (companyAdmin, user, Project Lead) needs the client experience,
  //    not the SystemAdminRouter. ClientDashboardNew surfaces a
  //    CompanyPicker when companyId is missing, so this is also how the
  //    operator gets prompted to choose which company to act as.
  //
  //    Without this branch the platform-safety guard below would catch
  //    every system admin and render SystemAdminRouter regardless of
  //    activeRole, defeating the entire view-as flow for client roles.
  const CLIENT_VIEW_AS_ROLES = new Set([
    'companyAdmin',
    'user',
    'Project Lead',
  ]);
  if (user?.isAdmin && CLIENT_VIEW_AS_ROLES.has(activeRole)) {
    const resolvedCompanyId =
      company?.id || user?.companyId || getPersistedCompany()?.id || null;
    const resolvedCompanyName =
      company?.name || getPersistedCompany()?.name || 'Your Company';
    console.log(
      '[ROLE_VIEW] selected router/page: ClientDashboardNew (system admin client view-as)',
      { activeRole, resolvedCompanyId: resolvedCompanyId || 'MISSING' },
    );
    return (
      <ClientShell name="ClientDashboard (view-as)">
        <ClientDashboardNew
          accessToken={accessToken}
          currentUser={user}
          companyId={resolvedCompanyId || ''}
          companyName={resolvedCompanyName}
          onLogout={handleLogout}
        />
      </ClientShell>
    );
  }

  // 5) Fallback — platform-safe guard for system admins, otherwise client / company user experience
  const isPlatformHost = isSystemAdminDomain();

  // On the platform/root host, system admins whose activeRole is NOT a
  // recognised view-as target (firm, billing, or client — handled above)
  // should never fall into client/tenant dashboards. This catches
  // unexpected/legacy activeRole values rather than every system admin.
  if (isPlatformHost && isSystemAdmin(user)) {
    console.log(
      '[ROLE_VIEW] platform system admin on root/admin domain with unrecognised activeRole, forcing SystemAdminRouter (activeRole=' +
        activeRole +
        ')',
    );
    return (
      <SystemAdminRouter
        accessToken={accessToken}
        currentUser={user}
        onLogout={handleLogout}
      />
    );
  }

  // Normal fallback — tenant / client experience (unchanged)
  const resolvedCompanyId = company?.id || user?.companyId || getPersistedCompany()?.id || null;
  const resolvedCompanyName = company?.name || getPersistedCompany()?.name || 'Your Company';

  // Persist for future refreshes if we have a valid company from authoritative sources
  if (resolvedCompanyId && (company?.id || user?.companyId)) {
    persistActiveCompany(resolvedCompanyId, resolvedCompanyName);
  }

  console.log('[ROLE_VIEW] selected router/page: ClientDashboardNew (fallback)', {
    companyId: resolvedCompanyId || 'MISSING',
    companyName: resolvedCompanyName,
    activeRole: user?.activeRole,
    userId: user?.id,
    source: company?.id
      ? 'company-prop'
      : user?.companyId
      ? 'user-prop'
      : getPersistedCompany()?.id
      ? 'localStorage'
      : 'none',
  });
  return (
    <ClientShell name="ClientDashboard">
      <ClientDashboardNew
        accessToken={accessToken}
        currentUser={user}
        companyId={resolvedCompanyId || ''}
        companyName={resolvedCompanyName}
        onLogout={handleLogout}
      />
    </ClientShell>
  );
}

export default function AppRouter() {
  const [route, setRoute] = useState<'loading' | 'marketing' | 'signup' | 'app'>('loading');
  const [tenantInfo, setTenantInfo] = useState<{ firmId: string; firmName: string } | null>(null);
  const [tenantNotFound, setTenantNotFound] = useState(false);
  
  // App state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [needsBootstrap, setNeedsBootstrap] = useState(false);
  const [accessToken, setAccessToken] = useState('');
  const [user, setUser] = useState<any>(null);
  const [company, setCompany] = useState<any>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  
  // Track current path for navigation - force re-renders when path changes
  const [currentPath, setCurrentPath] = useState(window.location.pathname);
  
  // Session expiration modal state
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  
  // Consultant view state - track if viewing client detail
  const [viewingClientId, setViewingClientId] = useState<string | null>(null);
  const [viewingSOWId, setViewingSOWId] = useState<string | null>(null);
  
  // Impersonation integration
  const { session: impersonationSession, isImpersonating } = useImpersonation();
  const [adminSession, setAdminSession] = useState<{token: string, user: any, company: any} | null>(null);

  // Handle impersonation state changes
  useEffect(() => {
    const handleImpersonationChange = async () => {
      // START IMPERSONATION
      if (isImpersonating && impersonationSession && !adminSession && accessToken && !accessToken.startsWith('imp_')) {
        console.log('[AppRouter] Starting impersonation of:', impersonationSession.targetUserEmail);
        
        // Backup admin session
        setAdminSession({
          token: accessToken,
          user: user,
          company: company
        });
        
        // Set new token
        setAccessToken(impersonationSession.token);
        
        // Fetch target user profile
        try {
          const response = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/validate-session`, {
             headers: getAuthHeaders(impersonationSession.token)
          });
          
          if (response.ok) {
             const data = await response.json();
             setUser(data.user);
             setCompany(data.company);
             console.log('[AppRouter] Impersonation profile loaded:', data.user.email);
          } else {
             console.error('Failed to load impersonation profile');
          }
        } catch (e) {
          console.error('Error loading impersonation profile:', e);
        }
      } 
      // END IMPERSONATION
      else if (!isImpersonating && adminSession) {
        console.log('[AppRouter] Ending impersonation, restoring admin session');
        setAccessToken(adminSession.token);
        setUser(adminSession.user);
        setCompany(adminSession.company);
        setAdminSession(null);
      }
    };
    
    handleImpersonationChange();
  }, [isImpersonating, impersonationSession, accessToken]);

  // ============================================
  // CROSS-TAB SESSION SYNCHRONIZATION
  // ============================================
  useEffect(() => {
    const sync = getCrossTabSync();
    
    console.log('[AppRouter] Setting up cross-tab session sync');
    
    // Subscribe to session events from other tabs
    const unsubscribe = sync.subscribe((event) => {
      console.log('[AppRouter] Cross-tab event received:', event.type);
      
      switch (event.type) {
        case 'login':
          // Another tab logged in - update our state
          if (!isAuthenticated) {
            console.log('[AppRouter] Another tab logged in, updating state');
            setAccessToken(event.sessionId);
            setUser(event.user);
            setCompany(event.company);
            setIsAuthenticated(true);
            setAuthState(true, event.user, event.company);
          }
          break;
          
        case 'logout':
          // Another tab logged out - clear our state
          if (isAuthenticated) {
            console.log('[AppRouter] Another tab logged out, clearing state');
            setAccessToken('');
            setUser(null);
            setCompany(null);
            setIsAuthenticated(false);
            setAuthState(false, null, null);
            // Force reload to show login screen
            setTimeout(() => window.location.reload(), 100);
          }
          break;
          
        case 'session_expired':
          // Session expired in another tab
          if (isAuthenticated) {
            console.log('[AppRouter] Session expired in another tab');
            setShowSessionExpiredModal(true);
          }
          break;
          
        case 'session_updated':
          // User data updated in another tab
          if (isAuthenticated) {
            console.log('[AppRouter] User data updated in another tab');
            setUser(event.user);
            setCompany(event.company);
          }
          break;
      }
    });
    
    // Clean up on unmount
    return () => {
      unsubscribe();
    };
  }, [isAuthenticated]); // Re-subscribe if auth state changes
  
  /**
   * Comprehensive logout function that properly signs out from Supabase
   * and clears all session data
   */
  const handleLogout = async () => {
    // A) Immediate diagnostic log (FIRST LINE)
    console.log('[LOGOUT_TRACE] ========== HANDLELOGOUT CALLED ==========');
    console.log('[LOGOUT_TRACE] handleLogout function started');
    console.log('[LOGOUT_TRACE] Stack trace:', new Error().stack);
    console.log('[LOGOUT] click received');
    console.log('[AppRouter] Starting comprehensive logout process...');
    
    // B) Read token from canonical key
    const token = localStorage.getItem('pathkit_session_token');
    console.log('[LOGOUT] Token found:', token ? `${token.substring(0, 20)}... (length: ${token.length})` : 'none');
    console.log('[LOGOUT_TRACE] About to call destroySession...');
    
    // Notify all tabs about logout FIRST
    const sync = getCrossTabSync();
    sync.notifyLogout();
    
    try {
      // C) Call backend to revoke session (best effort; do not block logout on failure)
      console.log('[LOGOUT] calling session/destroy');
      console.log('[LOGOUT_TRACE] Calling destroySession with token:', token || accessToken || '');
      try {
        await destroySession(token || accessToken || '');
        console.log('[LOGOUT] ✅ Backend session destroyed');
      } catch (error) {
        console.error('[LOGOUT] ⚠️  Backend session destroy failed (continuing anyway):', error);
        // Continue with logout even if backend destroy fails
      }
      
      // Sign out from Supabase to invalidate the auth session (best effort)
      console.log('[LOGOUT] Signing out from Supabase...');
      try {
        const { error } = await supabase.auth.signOut();
        if (error) {
          console.error('[LOGOUT] ⚠️  Supabase signOut error (continuing anyway):', error);
        } else {
          console.log('[LOGOUT] ✅ Supabase signOut successful');
        }
      } catch (error) {
        console.error('[LOGOUT] ⚠️  Supabase signOut failed (continuing anyway):', error);
      }
      
      // D) Clear ALL auth storage keys (canonical + legacy)
      console.log('[LOGOUT] clearing client auth state');
      
      // Use sessionManager's comprehensive clear function
      clearSessionFromLocalStorage();
      clearPersistedCompany();
      
      // VERIFICATION: Confirm canonical token was cleared (FIX B.3)
      console.log('[LOGOUT] Cleared token?', localStorage.getItem('pathkit_session_token') === null);
      
      // Also clear any Supabase auth storage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('sb-') || key.startsWith('supabase.')) {
          localStorage.removeItem(key);
        }
      });
      console.log('[LOGOUT] ✅ Cleared Supabase auth storage');
      
      // Clear sessionStorage
      sessionStorage.clear();
      console.log('[LOGOUT] ✅ Cleared sessionStorage');
      
      // E) Broadcast logout to other tabs (already done above via sync.notifyLogout())
      console.log('[LOGOUT] ✅ Broadcasted logout to other tabs');
      
      // Clear in-memory auth state (React state/store)
      console.log('[LOGOUT] Resetting in-memory state...');
      setIsAuthenticated(false);
      setAccessToken('');
      setUser(null);
      setCompany(null);
      setViewingClientId(null);
      console.log('[LOGOUT] ✅ In-memory state cleared');
      
      console.log('[LOGOUT] ✅ Logout complete, redirecting...');
      console.log('[LOGOUT] redirecting');
      
      // F) Hard redirect to ensure app resets
      // If already on /login, force reload. Otherwise go to /login
      if (window.location.pathname === '/login') {
        console.log('[LOGOUT] Already on /login, forcing reload');
        window.location.reload();
      } else {
        console.log('[LOGOUT] Redirecting to /login');
        window.location.href = '/login';
      }
      
    } catch (error) {
      console.error('[LOGOUT] ❌ Error during logout:', error);
      // Even if there's an error, try to clear all state and reload
      try {
        clearSessionFromLocalStorage();
      } catch (e) {
        console.error('[LOGOUT] ❌ Error clearing session:', e);
      }
      localStorage.clear();
      sessionStorage.clear();
      console.log('[LOGOUT] Force clearing all storage due to error');
      window.location.href = '/login';
    }
  };
  
  // Check for existing session with SOC2-compliant subdomain isolation
  useEffect(() => {
    const checkSession = async () => {
      try {
        console.log('[SESSION_BOOT] ========== APPROUTER CHECK SESSION START ==========');
        console.log('[SESSION_BOOT] Component mount timestamp:', Date.now());
        console.log('[SESSION_BOOT] Current sessionChecked state:', sessionChecked);
        console.log('[SESSION_BOOT] Current isAuthenticated state:', isAuthenticated);
        
        // ---------------------------------------------------------------
        // PHASE 1 — Optimistic hydration from localStorage (instant, 0 ms)
        // Lets the dashboard render immediately while the server validates
        // in the background.  If the server later says "invalid", we clear
        // state and redirect to login.
        // ---------------------------------------------------------------
        const cached = getSessionFromLocalStorage();
        console.log('[SESSION_BOOT] Phase 1 - Checking localStorage');
        console.log('[SESSION_BOOT] Token present:', !!cached.sessionId);
        console.log('[SESSION_BOOT] Cached user payload present:', !!cached.user);
        console.log('[SESSION_BOOT] Cached session:', {
          hasSessionId: !!cached.sessionId,
          hasUser: !!cached.user,
          hasCompany: !!cached.company,
          userRole: cached.user?.role
        });
        
        // If we have a session token (regardless of cached user data), we need to validate it
        if (cached.sessionId) {
          if (cached.user) {
            // We have BOTH token AND user data - optimistically hydrate
            console.log('[SESSION_BOOT] ✅ Found cached session with full user data, hydrating optimistically');
            setAccessToken(cached.sessionId);
            setUser(cached.user);
            setCompany(cached.company);
            setIsAuthenticated(true);
            setSessionChecked(true);
          } else {
            // We have a token but NO user data - this can happen if:
            // 1. User just logged in and user data hasn't been cached yet
            // 2. localStorage was partially cleared
            // 3. Session was stored but user data wasn't
            // We MUST validate with server before deciding if user is authenticated
            console.log('[SESSION_BOOT] ⏳ Found session token WITHOUT user data');
            console.log('[SESSION_BOOT] Entering auth-check loading state (will validate with server)');
            setSessionChecked(false); // Keep in loading state until server responds
          }
          // Don't return — continue to Phase 2 for server validation
        } else {
          console.log('[SESSION_BOOT] No session token found in localStorage');
          console.log('[SESSION_BOOT] User is definitely not authenticated');
        }

        // ---------------------------------------------------------------
        // PHASE 2 — Background server validation
        // ---------------------------------------------------------------
        console.log('[SESSION_BOOT] Phase 2 - Starting server validation');
        console.log('[SESSION_BOOT] Validation request started');
        const { performSessionCheck } = await import('./utils/appRouterSessionCheck');
        const sessionResult = await performSessionCheck();
        
        console.log('[SESSION_BOOT] Validation request completed');
        console.log('[SESSION_BOOT] Server validation result:', {
          isAuthenticated: sessionResult.isAuthenticated,
          hasSessionId: !!sessionResult.sessionId,
          hasUser: !!sessionResult.user,
          userRole: sessionResult.user?.role
        });
        
        if (sessionResult.isAuthenticated && sessionResult.sessionId) {
          // Update with authoritative server data (may have newer role, etc.)
          console.log('[SESSION_BOOT] ✅ Validation succeeded - updating app state with authoritative server data');
          setAccessToken(sessionResult.sessionId);
          setUser(sessionResult.user);
          setCompany(sessionResult.company);
          setIsAuthenticated(true);
          
          // CF_ADMIN_CONTEXT diagnostics — log firm context for firm roles
          const activeRole = sessionResult.user?.activeRole || sessionResult.user?.role;
          const firmRoles = ['cfAdmin', 'consultant', 'billingAdmin', 'bdRep', 'Engagement Lead', 'Engineer', 'leadership_marketing'];
          if (firmRoles.includes(activeRole)) {
            console.log('[CF_ADMIN_CONTEXT] === Boot Diagnostics ===');
            console.log('[CF_ADMIN_CONTEXT] role:', activeRole);
            console.log('[CF_ADMIN_CONTEXT] firmId:', sessionResult.user?.firmId || sessionResult.user?.consultingFirmId || 'UNDEFINED');
            console.log('[CF_ADMIN_CONTEXT] firmName:', sessionResult.user?.firmName || 'UNDEFINED');
            console.log('[CF_ADMIN_CONTEXT] source: server-validation (Phase 2)');
            console.log('[CF_ADMIN_CONTEXT] isAdmin:', sessionResult.user?.isAdmin);
            console.log('[CF_ADMIN_CONTEXT] baseRole:', sessionResult.user?.baseRole);
            if (!sessionResult.user?.firmId && !sessionResult.user?.consultingFirmId) {
              console.warn('[CF_ADMIN_CONTEXT] ⚠️ WARNING: Firm role active but no firmId — modules will degrade');
            }
          }
        } else if (cached.sessionId) {
          // We HAD a token but server says it's invalid
          // This is a DEFINITIVE failure - clear state and show login
          console.log('[SESSION_BOOT] ⚠️ Validation failed definitively - server says session is invalid');
          console.log('[SESSION_BOOT] Clearing auth state (token was:', cached.sessionId.substring(0, 20), '...)');
          setAccessToken('');
          setUser(null);
          setCompany(null);
          setIsAuthenticated(false);
        } else {
          console.log('[SESSION_BOOT] Not authenticated (no token found, no server session)');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('[SESSION_BOOT] ❌ ERROR during checkSession:', error);
        console.error('[SESSION_BOOT] Error stack:', error instanceof Error ? error.stack : 'no stack');
        // If we already hydrated optimistically, keep the user logged in
        // (network glitch shouldn't kick them out)
        if (!accessToken) {
          console.log('[SESSION_BOOT] No access token, setting isAuthenticated=false');
          setIsAuthenticated(false);
        } else {
          console.log('[SESSION_BOOT] Have access token, keeping user logged in despite error');
        }
      } finally {
        console.log('[SESSION_BOOT] ========== APPROUTER CHECK SESSION END ==========');
        setSessionChecked(true);
      }
    };

    checkSession();
  }, []);

  // Periodic session validation - check every 5 minutes
  useEffect(() => {
    if (!isAuthenticated || !accessToken) {
      return;
    }

    const validateSession = async () => {
      try {
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/validate-session`,
          {
            headers: getAuthHeaders(accessToken),
          }
        );

        if (!response.ok) {
          console.log('AppRouter - Session expired');
          
          // Check for unsaved changes before logging out
          if (unsavedChangesManager.hasUnsavedChanges()) {
            console.log('AppRouter - Has unsaved changes, showing modal');
            // Save all drafts immediately
            unsavedChangesManager.saveAllDraftsToStorage();
            // Show modal instead of immediately logging out
            setShowSessionExpiredModal(true);
          } else {
            console.log('AppRouter - No unsaved changes, logging out and refreshing page');
            // Clear session (both old and new formats)
            localStorage.removeItem('pathkit_access_token');
            localStorage.removeItem('pathkit_session_id');
            localStorage.removeItem('pathkit_user');
            localStorage.removeItem('pathkit_company');
            setAccessToken('');
            setUser(null);
            setCompany(null);
            setIsAuthenticated(false);
            
            // Force page refresh to show login screen
            window.location.reload();
          }
        } else {
          // Session is still valid - update user data if needed
          const data = await response.json();
          if (data.user) {
            localStorage.setItem('pathkit_user', JSON.stringify(data.user));
            setUser(data.user);
          }
        }
      } catch (error) {
        console.error('AppRouter - Error validating session:', error);
        // On network error, don't log out - could be temporary
      }
    };

    // Check session every 5 minutes (300000 ms)
    const intervalId = setInterval(validateSession, 5 * 60 * 1000);

    // Cleanup on unmount
    return () => clearInterval(intervalId);
  }, [isAuthenticated, accessToken]);

  useEffect(() => {
    const initialize = async () => {
      // Check URL path
      const path = window.location.pathname;
      // console.log('AppRouter - Initializing with path:', path);
      
      // ADMIN DOMAIN HANDLING: If on admin.pathkit.pro, treat it like the marketing route
      // but default "/" to "/system-admin" for better UX
      if (isAdminDomain()) {
        console.log('AppRouter - Detected admin.pathkit.pro domain');
        
        // If root path, redirect to /system-admin
        if (path === '/') {
          console.log('AppRouter - Redirecting admin.pathkit.pro/ to /system-admin');
          window.history.replaceState({}, '', '/system-admin');
          // Fall through to handle as /system-admin
        }
        
        // Route to marketing to handle all admin paths
        setRoute('marketing');
        return;
      }
      
      // Check for signup route
      if (path === '/signup' || path === '/get-started') {
        // console.log('AppRouter - Showing signup page');
        setRoute('signup');
        return;
      }

      // Check for user recovery route
      if (path === '/recover' || path === '/user-recovery') {
        // console.log('AppRouter - Showing user recovery page');
        setRoute('marketing'); // Use marketing route to bypass tenant check
        return;
      }

      // Check for password reset route
      if (path === '/reset-password' || path === '/forgot-password') {
        // console.log('AppRouter - Rendering PasswordReset');
        setRoute('marketing');
        return;
      }
      
      // CEL onboarding page (public, no auth required - magic link destination)
      if (path === '/onboarding/cel' || path === '/onboarding/team') {
        console.log('AppRouter - CEL onboarding route detected (public)');
        setRoute('marketing');
        return;
      }

      // Check for public SOW viewer route
      if (path.startsWith('/sow/')) {
        // console.log('AppRouter - Rendering ClientSOWViewer');
        setRoute('marketing');
        return;
      }
      
      // Check for quick setup route
      if (path === '/setup' || path === '/quick-setup') {
        // console.log('AppRouter - Showing quick subdomain setup page');
        setRoute('marketing'); // Use marketing route to bypass tenant check
        return;
      }

      // Check for diagnostic route
      if (path === '/diagnostic' || path === '/check-account') {
        // console.log('AppRouter - Showing account diagnostic page');
        setRoute('marketing'); // Use marketing route to bypass tenant check
        return;
      }


      // Check for admin tools route
      if (path === '/admin-tools' || path === '/tools') {
        // console.log('AppRouter - Showing admin tools page');
        setRoute('marketing'); // Use marketing route to bypass tenant check
        return;
      }

      // Check for notification preferences route
      if (path === '/preferences' || path === '/notifications' || path === '/notification-preferences') {
        // console.log('AppRouter - Showing notification preferences page');
        setRoute('marketing'); // Use marketing route to bypass tenant check
        return;
      }

      // Internal test harness
      if (path === '/internal/testing') {
        setRoute('marketing');
        return;
      }

      // Internal control grid (read-only product health view)
      if (path === '/internal/control-grid') {
        setRoute('marketing');
        return;
      }

      // Check for system admin dashboard route (public, no auth required)
      if (path === '/system-admin' || path === '/admin-dashboard') {
        // console.log('AppRouter - Showing system admin dashboard (public)');
        setRoute('marketing'); // Use marketing route to bypass tenant check
        return;
      }
      
      // Check for admin panel route (requires authentication)
      if (path === '/admin-panel' || path === '/system-admin-panel') {
        // console.log('AppRouter - Admin panel route detected, setting marketing route');
        setRoute('marketing'); // Will be handled by SystemAdminRouter check in render
        return;
      }
      
      // Check for support, privacy, and terms pages
      if (path === '/support' || path === '/help' || 
          path === '/privacy' || path === '/privacy-policy' ||
          path === '/terms' || path === '/terms-of-service' ||
          path === '/ai-trust' || path === '/ai-data-use') {
        // console.log('AppRouter - Showing legal/support page');
        setRoute('marketing'); // Use marketing route to bypass tenant check
        return;
      }
      
      // Admin panel requires authentication - don't force marketing route
      // Will be handled by SystemAdminRouter when authenticated

      // Check for public routes (book-demo, oauth-test, etc) - these should show marketing site
      if (path === '/book-demo' || path === '/demo') {
        // console.log('AppRouter - Showing marketing site for booking');
        setRoute('marketing');
        return;
      }

      // OAuth test page (special route)
      if (path === '/oauth-test' || path === '/test-oauth') {
        // console.log('AppRouter - Showing OAuth test page');
        setRoute('marketing'); // Use marketing route to bypass auth
        return;
      }


      // Check for tenant subdomain
      const subdomain = getTenantFromHostname();
      
      if (!subdomain) {
        // No subdomain - show marketing site
        // console.log('AppRouter - No subdomain, showing marketing site');
        setRoute('marketing');
        return;
      }

      // console.log('AppRouter - Found subdomain:', subdomain);

      // -----------------------------------------------------------------
      // Optimistic tenant hydration: if we already resolved this subdomain
      // in a prior page load, use the cached value immediately so the
      // dashboard can start rendering.  We still fire the server lookup in
      // the background to keep the cache fresh.
      // -----------------------------------------------------------------
      const TENANT_CACHE_KEY = `pathkit_tenant_${subdomain}`;
      const cachedTenantStr = localStorage.getItem(TENANT_CACHE_KEY);
      let usedCache = false;
      if (cachedTenantStr) {
        try {
          const cachedTenant = JSON.parse(cachedTenantStr);
          if (cachedTenant?.firmId) {
            setTenantInfo(cachedTenant);
            setRoute('app');
            usedCache = true;
            // Don't return — fall through to background refresh
          }
        } catch { /* corrupt cache, ignore */ }
      }

      // Fetch tenant info (foreground if no cache, background if cached)
      const tenant = await getTenantCompanyId(subdomain);
      
      if (tenant) {
        // Persist for next page load
        localStorage.setItem(TENANT_CACHE_KEY, JSON.stringify(tenant));
        setTenantInfo(tenant);
        setRoute('app');
      } else if (!usedCache) {
        // console.log('AppRouter - Tenant not found');
        setTenantNotFound(true);
        setRoute('loading'); // Keep on loading to show error
      }
    };

    initialize();
  }, []);

  // Listen for navigation changes (back/forward, pushState)
  useEffect(() => {
    const handleNavigation = () => {
      const path = window.location.pathname;
      console.log('AppRouter - Navigation detected, path:', path);
      
      // Update currentPath state to force re-render
      setCurrentPath(path);
      
      // If authenticated system admin navigates to root, redirect to
      // /system-admin — but ONLY when their active view role is also
      // systemAdmin. A system admin in view-as mode (acting as a client
      // or firm role) needs to stay on `/` so renderViewForActiveRole
      // can show the role-appropriate UI; auto-redirecting them back to
      // /system-admin would re-trap them in the System Admin panel.
      if (
        path === '/' &&
        isAuthenticated &&
        isSystemAdmin(user) &&
        isActiveViewSystemAdmin(user)
      ) {
        console.log('AppRouter - System admin at root, redirecting to /system-admin');
        window.history.replaceState({}, '', '/system-admin');
        setCurrentPath('/system-admin');
        return;
      }
      
      // Don't interfere with system admin routes - let normal routing handle it
      if (path === '/system-admin' || path === '/admin-dashboard') {
        console.log('AppRouter - System admin route detected, not resetting route');
        return;
      }
      
      // Don't interfere with company dashboard routes - let normal routing handle it
      if (path.startsWith('/company/')) {
        console.log('AppRouter - Company dashboard route detected, not resetting route');
        return;
      }
      
      // For admin panel routes when authenticated, force re-render
      if ((path === '/admin-panel' || path === '/system-admin-panel') && isAuthenticated) {
        console.log('AppRouter - Navigated to admin panel while authenticated, triggering re-render');
        return; // currentPath update will trigger re-render
      }
      
      // For other admin panel routes, only reset if not authenticated
      if ((path === '/admin-panel' || path === '/system-admin-panel') && !isAuthenticated) {
        console.log('AppRouter - Navigated to admin panel while not authenticated, resetting');
        setRoute('loading');
        setTimeout(() => setRoute('marketing'), 0);
      }
    };

    // Listen for browser back/forward
    window.addEventListener('popstate', handleNavigation);
    
    // Listen for custom navigation event (pushState)
    window.addEventListener('pathkit-navigate', handleNavigation);

    return () => {
      window.removeEventListener('popstate', handleNavigation);
      window.removeEventListener('pathkit-navigate', handleNavigation);
    };
  }, [isAuthenticated, user]);

  // Handle signup completion
  const handleSignupComplete = (subdomain: string) => {
    // Redirect to tenant subdomain
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('.vercel.app')) {
      window.location.href = `${window.location.origin}?tenant=${subdomain}`;
    } else {
      window.location.href = `https://${subdomain}.pathkit.pro`;
    }
  };

  // Handle login - now with SOC2-compliant session isolation
  const handleLogin = async (sessionId: string, userData: any, companyData: any) => {
    console.log('AppRouter - Login handler called with sessionId:', sessionId);
    console.log('AppRouter - User data:', userData);
    console.log('AppRouter - Company data:', companyData);
    
    // ENTERPRISE AUTH: Store session using canonical helper
    storeSessionToken(sessionId);
    
    // LEGACY: Also store in old localStorage keys for backward compatibility
    localStorage.setItem('pathkit_session_id', sessionId);
    localStorage.setItem('pathkit_user', JSON.stringify(userData));
    if (companyData) {
      localStorage.setItem('pathkit_company', JSON.stringify(companyData));
    }
    console.log('[AppRouter] Session stored using canonical key + legacy keys');
    
    // Store session ID (not Supabase token)
    setAccessToken(sessionId);
    setUser(userData);
    setCompany(companyData);
    setIsAuthenticated(true);
    
    // Store in auth state for components
    setAuthState(true, userData, companyData);
    
    console.log('AppRouter - Login complete, user authenticated');
    
    // System admins should be redirected to /system-admin after login
    // Check both direct role AND isAdmin flag (for system admins who switched roles)
    if (userData.role === 'systemAdmin' || userData.role === 'system_admin' || userData.isAdmin === true) {
      console.log('AppRouter - System admin logged in, navigating to /system-admin');
      console.log('[ROLE_SWITCH_FLOW] handleLogin detected system admin, role:', userData.role, 'isAdmin:', userData.isAdmin);
      // Use pushState instead of full page reload to preserve state
      window.history.pushState({}, '', '/system-admin');
      // Trigger custom navigation event
      window.dispatchEvent(new Event('pathkit-navigate'));
      return;
    }
    
    // NOTE: We no longer redirect users to different subdomains automatically.
    // The new session isolation system validates that users can ONLY log into
    // subdomains they're authorized for. If a user tries to log into the wrong
    // subdomain, the session creation will fail with a 403 error.
    //
    // This ensures SOC2 compliance by preventing cross-tenant access.
    
    // Show onboarding for new companies only if they haven't completed it
    if (companyData && companyData.created_at) {
      const createdAt = new Date(companyData.created_at);
      const daysSinceCreation = (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24);
      
      if (daysSinceCreation < 1) {
        // Check if onboarding is already completed
        try {
          const onboardingResponse = await fetch(
            `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/company-data/${companyData.id}/onboarding-state`,
            {
              headers: getAuthHeaders(sessionId),
            }
          );
          
          if (onboardingResponse.ok) {
            const onboardingState = await onboardingResponse.json();
            // Only show onboarding if not completed
            if (onboardingState.currentStep !== 'completed') {
              setShowOnboarding(true);
            }
          } else {
            // No onboarding state exists yet, show it
            setShowOnboarding(true);
          }
        } catch (error) {
          console.error('[AppRouter] Error checking onboarding state:', error);
          // On error, don't show onboarding to avoid annoying users
        }
      }
    }
  };

  // Handle onboarding complete/skip
  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
  };

  // Loading state
  if (route === 'loading') {
    if (tenantNotFound) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md px-6">
            <div className="text-6xl mb-4">🔍</div>
            <h1 className="text-3xl mb-4">Tenant Not Found</h1>
            <p className="text-gray-600 mb-6">
              The subdomain you're trying to access doesn't exist or hasn't been configured yet.
            </p>
            <a 
              href="https://pathkit.pro" 
              className="text-blue-600 hover:text-blue-700 underline"
            >
              Return to Pathkit Home
            </a>
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-2xl mb-2">Loading...</div>
          <div className="text-gray-600">Initializing Pathkit</div>
        </div>
      </div>
    );
  }

  // Signup page
  if (route === 'signup') {
    return <SignupPage onSignupComplete={handleSignupComplete} />;
  }

  // Marketing site (no subdomain)
  if (route === 'marketing') {
    // Check for special routes that should bypass everything first
    const path = window.location.pathname;
    // console.log('AppRouter - Marketing route, checking path:', path);
    
    if (path === '/oauth-test' || path === '/test-oauth') {
      // console.log('AppRouter - Rendering OAuthQuickTest');
      return <OAuthQuickTest />;
    }
    
    if (path === '/recover' || path === '/user-recovery') {
      // console.log('AppRouter - Rendering UserRecovery');
      return <UserRecovery />;
    }
    
    if (path === '/reset-password' || path === '/forgot-password') {
      // console.log('AppRouter - Rendering PasswordReset');
      return <PasswordReset />;
    }
    
    // CEL onboarding page (public, no auth required - magic link destination)
    if (path === '/onboarding/cel' || path === '/onboarding/team') {
      console.log('AppRouter - Rendering CELOnboardingPage');
      return (
        <ClientShell name="CELOnboardingPage">
          <CELOnboardingPage />
        </ClientShell>
      );
    }

    if (path.startsWith('/sow/')) {
      // console.log('AppRouter - Rendering ClientSOWViewer');
      const shareToken = path.split('/sow/')[1];
      return <ClientSOWViewer shareToken={shareToken} />;
    }
    
    if (path === '/setup' || path === '/quick-setup') {
      // console.log('AppRouter - Rendering QuickSubdomainSetup');
      return <QuickSubdomainSetup />;
    }
    
    // The account lookup and the admin tools are System Admin › Operations now (RESTORE_PLAN R3.6)
    if (path === '/diagnostic' || path === '/check-account' || path === '/admin-tools' || path === '/tools') {
      return <LegacyAdminRedirect />;
    }
    
    if (path === '/preferences' || path === '/notifications' || path === '/notification-preferences') {
      // console.log('AppRouter - Rendering NotificationPreferencesPage');
      return <NotificationPreferencesPage />;
    }

    // Internal test harness
    if (path === '/internal/testing') {
      return <InternalTestHarness />;
    }

    // Internal control grid (read-only product health view)
    if (path === '/internal/control-grid') {
      return <ControlGridPage />;
    }

    if (path === '/privacy' || path === '/privacy-policy') {
      return <PrivacyPolicy />;
    }

    if (path === '/ai-trust' || path === '/ai-data-use') {
      return <AIDataUse />;
    }

    if (path === '/terms' || path === '/terms-of-service') {
      return <TermsOfService />;
    }

    if (path === '/book-demo' || path === '/demo') {
      return <BookDemo />;
    }
    
    // Records and orphans are System Admin › Operations now (RESTORE_PLAN R3.6)
    if (path === '/database-diagnostic' || path === '/db-check' || path === '/data-diagnostic' || path === '/data-check') {
      return <LegacyAdminRedirect />;
    }
    
    // System Admin routes - use SystemAdminRouter when authenticated
    if (path === '/system-admin' || path === '/admin-dashboard') {
      // Check if there's a token in the URL - if so, we MUST wait for session check
      const urlParams = new URLSearchParams(window.location.search);
      const tokenInUrl = urlParams.get('token');
      
      // CRITICAL: If there's a token in the URL, we MUST wait for session check to complete
      // Do NOT render anything until the token has been processed
      if (tokenInUrl) {
        console.log('AppRouter - Token found in URL, MUST wait for session processing...', {
          tokenPreview: `${tokenInUrl.substring(0, 20)}...`,
          sessionChecked
        });
        
        // Always show loading screen when there's a token, even if sessionChecked is true
        // This ensures the token gets processed before we render anything
        if (!isAuthenticated) {
          return (
            <div className="min-h-screen flex items-center justify-center">
              <div className="text-center">
                <div className="text-2xl mb-2">Authenticating...</div>
                <div className="text-gray-600">Processing authentication token...</div>
              </div>
            </div>
          );
        }
      }
      
      // Wait for session check to complete before deciding what to show
      if (!sessionChecked) {
        console.log('AppRouter - Waiting for session check before showing system-admin...', {
          sessionChecked,
          hasTokenInUrl: !!tokenInUrl,
          tokenPreview: tokenInUrl ? `${tokenInUrl.substring(0, 20)}...` : 'none'
        });
        return (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="text-2xl mb-2">Loading...</div>
              <div className="text-gray-600">
                {tokenInUrl ? 'Authenticating with token...' : 'Checking authentication...'}
              </div>
            </div>
          </div>
        );
      }
      
      console.log('AppRouter - Session check complete for /system-admin:', {
        isAuthenticated,
        userRole: user?.role,
        userActiveRole: user?.activeRole,
        userIsAdmin: user?.isAdmin,
        userBaseRole: user?.baseRole,
        hasTokenInUrl: !!tokenInUrl
      });
      
      // AUTHORIZATION: isSystemAdmin() checks isAdmin flag / base privilege
      // VIEW SELECTION: renderViewForActiveRole() uses activeRole to pick the UI
      if (isAuthenticated && isSystemAdmin(user)) {
        console.log('AppRouter - System admin AUTHORIZED on /system-admin');
        console.log('[LOGOUT_TRACE] AppRouter passing handleLogout (system-admin route)');
        // Render based on activeRole — NOT always SystemAdminRouter
        return renderViewForActiveRole(user, accessToken, company, handleLogout);
      }
      
      // Not signed in as a system admin: sign in first. The old public dashboard is retired; its tools are
      // System Admin › Tenants and Operations (RESTORE_PLAN R3.6).
      console.log('AppRouter - /system-admin without a system admin session: showing sign-in');
      return <Login onLogin={handleLogin} />;
    }
    
    if (path === '/login') {
      // ENTERPRISE LOGIN: the email-first login experience (the legacy options page is gone, step 6)
      console.log('AppRouter - Rendering new enterprise Login page');
      return <Login onLogin={handleLogin} />;
    }
    
    if (path === '/bootstrap') {
      // console.log('AppRouter - Rendering Bootstrap page');
      return <Bootstrap onComplete={() => {
        // Redirect to login after bootstrap
        window.location.href = '/login';
      }} />;
    }
    
    // Check for mockup viewer route
    // Check for admin panel routes - handle authentication
    if (path === '/admin-panel' || path === '/system-admin-panel') {
      // If user is authenticated as system admin, use SystemAdminRouter
      console.log('AppRouter - Admin panel route check:', { 
        isAuthenticated, 
        userRole: user?.role,
        userEmail: user?.email,
        hasAccessToken: !!accessToken,
        user 
      });
      if (isAuthenticated && isSystemAdmin(user)) {
        console.log('AppRouter - System admin AUTHORIZED on /admin-panel');
        console.log('[LOGOUT_TRACE] AppRouter passing handleLogout (admin-panel route)');
        // Render based on activeRole — NOT always SystemAdminRouter
        return renderViewForActiveRole(user, accessToken, company, handleLogout);
      }
      
      // Otherwise, show login prompt
      // console.log('AppRouter - Admin panel requires login, redirecting...');
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center max-w-md p-8">
            <Shield className="h-16 w-16 mx-auto mb-4 text-blue-600" />
            <h1 className="text-2xl font-semibold mb-2">Authentication Required</h1>
            <p className="text-gray-600 mb-6">
              The System Admin Panel requires authentication. Please log in to access admin features.
            </p>
            <a href="/login">
              <Button>Go to Login</Button>
            </a>
          </div>
        </div>
      );
    }
    
    // Default to marketing site
    // console.log('AppRouter - Rendering marketing site');
    
    // CATCH-ALL for authenticated system admins on any path.
    // When a system admin switches to a firm role (e.g. cfAdmin) on root domain,
    // renderViewForActiveRole returns a <RouterProvider> whose catch-all may
    // change the URL (e.g. /system-admin → /dashboard via <Navigate>).
    // If Phase 2 of the session boot triggers a re-render AFTER that URL change,
    // the path no longer matches /system-admin, so it would fall through here.
    // This catch-all ensures we still render the correct role-based view
    // instead of showing <Login />.
    if (isAuthenticated && isSystemAdmin(user)) {
      console.log('[ROLE_SWITCH_FLOW] Authenticated admin catch-all on marketing path:', path);
      console.log('[ROLE_SWITCH_FLOW] Rendering role view instead of falling through to Login');
      return renderViewForActiveRole(user, accessToken, company, handleLogout);
    }

    // Public marketing homepage (apex / www). Anything else falls through to Login
    // so unrecognized paths still surface the auth UX.
    if (path === '/' || path === '') {
      return (
        <>
          <MarketingHomepage />
        </>
      );
    }

    return (
      <>
        <Login onLogin={handleLogin} />
      </>
    );
  }

  // Tenant application
  if (route === 'app') {
    // Check if system needs bootstrap
    if (needsBootstrap) {
      return <Bootstrap onComplete={() => setNeedsBootstrap(false)} />;
    }

    // Wait for session check before showing login
    if (!sessionChecked) {
      return (
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="text-2xl mb-2">Loading...</div>
            <div className="text-gray-600">Checking session</div>
          </div>
        </div>
      );
    }

    // Not authenticated - show login
    if (!isAuthenticated) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          {tenantInfo && (
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold mb-2">{tenantInfo.firmName}</h2>
              <p className="text-gray-600">Sign in to your Pathkit account</p>
            </div>
          )}
          <Login onLogin={handleLogin} />
        </div>
      );
    }

    // Authenticated - show app with onboarding if needed
    return (
      <>
        <ImpersonationBanner />
        {/* Your actual application goes here */}
        {/* This is a placeholder - you'll need to render your actual app component */}
        <div className="min-h-screen">
          {renderViewForActiveRole(user, accessToken, company, handleLogout)}
        </div>
        
        {/* Debug Info - shows current user role and permissions */}
        <UserDebugInfo currentUser={user} accessToken={accessToken} />

        {/* Onboarding wizard overlay */}
        {showOnboarding && tenantInfo && (
          <OnboardingWizard
            companyName={tenantInfo.firmName}
            subdomain={getTenantFromHostname() || ''}
            onComplete={handleOnboardingComplete}
            onSkip={handleOnboardingComplete}
          />
        )}
        
        {/* Session Expiration Modal - shown when session expires with unsaved changes */}
        <SessionExpirationModal
          isOpen={showSessionExpiredModal}
          onSaveDrafts={() => {
            console.log('AppRouter - User confirmed drafts are saved');
            unsavedChangesManager.saveAllDraftsToStorage();
            setShowSessionExpiredModal(false);
            // Redirect to login after a brief delay
            setTimeout(() => {
              window.location.reload();
            }, 500);
          }}
          onReauthenticate={() => {
            console.log('AppRouter - User chose to re-authenticate');
            unsavedChangesManager.saveAllDraftsToStorage();
            setShowSessionExpiredModal(false);
            // Redirect to login page
            window.location.reload();
          }}
          onDiscard={() => {
            console.log('AppRouter - User chose to discard changes');
            unsavedChangesManager.clearAllUnsavedChanges();
            setShowSessionExpiredModal(false);
            // Clear session and reload
            localStorage.removeItem('pathkit_access_token');
            localStorage.removeItem('pathkit_session_id');
            localStorage.removeItem('pathkit_user');
            localStorage.removeItem('pathkit_company');
            window.location.reload();
          }}
        />
      </>
    );
  }

  return null;
}